import {
  CloseOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Avatar, Divider, Image } from 'antd';
import dayjs from 'dayjs';
import { Fragment, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { assetsApi, inquiryFeeApi, itemInquiryMessageApi, itemMessageGroupApi } from '../../../../../apis';
import {
  ChangeThreadTypeDtoOptionTypeEnum,
  ItemInquiryMessages,
  ItemInternalMessages,
  ItemMessageGroup,
  ProposeInquiryFeeDtoTypeEnum,
} from '../../../../../apis/client-axios';
import CustomConfirmModel from '../../../../../components/modals/confirmModel';
import NotificationSuccess from '../../../../../components/notifications/NotificationSuccess';
import { RootState } from '../../../../../store';
import { QUERY_KEY, TIME_FORMAT } from '../../../../../util/constants';
import { Helper } from '../../../../../util/helper';
import { useIntl } from 'react-intl';
import { socketConfig } from '../../../../../webSocket/webSocket.config';
import { ChannelRegister } from '../../../../../webSocket/webSocket.constants';
import { ChatItemContext } from '..';
import { CardComposer, ComposerFile, InquiryThreadCard } from './InquiryThreadCard';
import { FEE_STATUS, ThreadTimeline } from './inquiryFeeTypes';
import { CHANGEABLE_INQUIRY_OPTIONS, getInquiryOption, InquiryOption } from './inquiryOptions';

// Icon header cho thread お問い合わせ chung (message icon, theo màu chủ đạo #1c86ed qua currentColor).
const GeneralInquiryIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M8 9h8" />
    <path d="M8 13h6" />
    <path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12z" />
  </svg>
);

export interface InquiryTabProps {
  listMessage: ItemInquiryMessages[];
  setListMesage: React.Dispatch<React.SetStateAction<ItemInquiryMessages[] | [] | ItemInternalMessages[]>>;
  totalReponse: number;
  setTotalResponse: React.Dispatch<React.SetStateAction<number>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  size: number;
  setSkipLoad: React.Dispatch<React.SetStateAction<boolean>>;
  skipLoad: boolean;
  setFileUploads: React.Dispatch<React.SetStateAction<{ privew: string; source: File }[] | undefined>>;
  fileUploads: { privew: string; source: File }[] | undefined;
  scrollToEnd: boolean;
  groupSelect: ItemMessageGroup | undefined;
}
export const InquiryTab = () => {
  const {
    listMessage,
    setListMesage,
    totalReponse,
    setTotalResponse,
    page,
    setPage,
    size,
    skipLoad,
    setSkipLoad,
    fileUploads,
    setFileUploads,
    scrollToEnd,
    groupSelect,
    staffById,
  } = useContext(ChatItemContext);
  const intl = useIntl();
  const { id } = useParams();
  const dummy = useRef(null);
  const { authUser } = useSelector((state: RootState) => state.auth);
  const [visibleSender, setVisibleSender] = useState(false);
  const [previewSender, setPreviewSender] = useState<{ source: string; index: number } | undefined>(undefined);
  const [visibleReceiver, setVisibleReceiver] = useState(false);
  const [previewReceiver, setPreviewReceiver] = useState<{ source: string; index: number } | undefined>(undefined);
  // Tạm ngưng ghim đáy khi đang tải tin cũ (prepend) — nếu không sẽ giật ngược về đáy.
  const pauseStickRef = useRef(false);
  const { data, isLoading } = useQuery({
    queryKey: [QUERY_KEY.LIST_GROUP_ITEM_MESSAGE_INQUIRY, page],
    queryFn: () =>
      itemInquiryMessageApi.itemInquiryControllerGetAll(groupSelect?.id, page, size).then((res: any) => {
        let response: any = res;
        if (!!(res?.data as any)?.content?.length) {
          response.data.content = (res?.data as any)?.content.reverse();
        }
        return response;
      }),
    enabled: !!groupSelect?.id && groupSelect?.type == 0 && authUser?.role != 'operation',
    staleTime: 1000,
  });

  // Danh sách thread (option) + timeline merge của item — hiển thị dạng card, giữ song song list phẳng cũ.
  const { data: threadData, refetch: refetchThreads } = useQuery({
    queryKey: ['LIST_INQUIRY_FEE_THREAD', id],
    queryFn: () => inquiryFeeApi.inquiryFeeControllerGetByStorageItem(+(id as string)).then((res: any) => res?.data),
    enabled: !!id && groupSelect?.type === 0 && authUser?.role !== 'operation',
    staleTime: 1000,
  });

  // Bóc mảng thread từ response (chịu được nhiều shape: array trực tiếp / {data} / {content}).
  const rawThreads: any[] = Array.isArray(threadData)
    ? threadData
    : Array.isArray((threadData as any)?.data)
      ? (threadData as any).data
      : Array.isArray((threadData as any)?.content)
        ? (threadData as any).content
        : [];

  // Chuẩn hoá + chỉ render card cho thread option (có optionType). Thread chung hiển ở list phẳng như cũ.
  const threads: ThreadTimeline[] = rawThreads
    .map((t: any): ThreadTimeline => {
      const optionType =
        t?.optionType ?? t?.option_type ?? t?.itemMessageGroup?.optionType ?? t?.itemMessageGroup?.option_type;
      return {
        itemMessageGroupId: t?.itemMessageGroupId ?? t?.item_message_group_id ?? t?.itemMessageGroup?.id ?? t?.id,
        optionType,
        status: t?.status ?? t?.itemMessageGroup?.status,
        timeline: Array.isArray(t?.timeline) ? t.timeline : [],
      };
    })
    .filter((t) => !!t?.optionType && !!t?.itemMessageGroupId);

  const proposeFee = useMutation({
    mutationFn: (dto: {
      itemMessageGroupId: number;
      type: ProposeInquiryFeeDtoTypeEnum;
      chargeAmount: number;
      note?: string;
    }) => inquiryFeeApi.inquiryFeeControllerPropose(dto),
    onSuccess: () => refetchThreads(),
  });

  // Báo giá lại: supersede quote PROPOSED cũ + tạo quote mới (PATCH admin/:id).
  // Không dùng propose lần 2 (BE chặn THREAD_ALREADY_HAS_OPEN_QUOTE).
  const reproposeFee = useMutation({
    mutationFn: (payload: { id: number; chargeAmount: number; note?: string }) =>
      inquiryFeeApi.inquiryFeeControllerReproposed(payload.id, {
        chargeAmount: payload.chargeAmount,
        note: payload.note,
      }),
    onSuccess: () => refetchThreads(),
  });

  // Hoàn thành công việc (chỉ khi fee đã PAID) — BE: POST admin/:id/complete.
  const completeFee = useMutation({
    mutationFn: (feeId: number) => inquiryFeeApi.inquiryFeeControllerComplete(feeId),
    onSuccess: () => refetchThreads(),
  });

  // Đổi loại option của thread — BE: PATCH item-message-group/admin/change-type.
  // BE tự gộp vào thread ĐANG MỞ cùng loại (nếu có) và supersede các báo giá cũ.
  const [pendingTypeChange, setPendingTypeChange] = useState<
    { thread: ThreadTimeline; fromLabel: string; toOption: InquiryOption } | undefined
  >(undefined);

  const changeThreadType = useMutation({
    mutationFn: (payload: { itemMessageGroupId: number; optionType: ChangeThreadTypeDtoOptionTypeEnum }) =>
      itemMessageGroupApi.itemMessageGroupControllerChangeType(payload),
    onSuccess: () => {
      setPendingTypeChange(undefined);
      refetchThreads();
      NotificationSuccess({ content: intl.formatMessage({ id: 'common.updateSuccess' }) });
    },
    onError: () => setPendingTypeChange(undefined),
  });

  const requestTypeChange = (thread: ThreadTimeline, optionType: string) => {
    const toOption = CHANGEABLE_INQUIRY_OPTIONS.find((o) => o.value === optionType);
    if (!toOption) return;
    setPendingTypeChange({
      thread,
      fromLabel: getInquiryOption(thread.optionType)?.label || '',
      toOption,
    });
  };

  const handleThreadSend = async (thread: ThreadTimeline, text: string, files: ComposerFile[], price?: number) => {
    // Có nhập giá → báo giá trên thread option; note = text kèm theo.
    if (price != null && !Number.isNaN(price) && price > 0 && thread?.optionType) {
      // Nếu thread đã có quote đang mở (PROPOSED) → báo giá LẠI (repropose), tiền tính theo lần cuối.
      const openFee = thread.timeline.find((item) => item.kind === 'fee' && item.data?.status === FEE_STATUS.PROPOSED);
      if (openFee?.data?.id != null) {
        reproposeFee.mutate({ id: openFee.data.id, chargeAmount: price, note: text || undefined });
        return;
      }
      proposeFee.mutate({
        itemMessageGroupId: thread.itemMessageGroupId,
        type: thread.optionType as ProposeInquiryFeeDtoTypeEnum,
        chargeAmount: price,
        note: text || undefined,
      });
      return;
    }
    if (!text && !files.length) return;

    // Không có giá → gửi tin nhắn thường vào thread (upload ảnh/PDF trước nếu có).
    let listUpload: any[] = [];
    if (files.length) {
      listUpload = await Promise.all(
        files.map((f) =>
          assetsApi.assetControllerUploadFile(f.source, 'chat-support').then((rs: any) => ({
            source: rs?.data?.srouce || rs?.data?.preview,
            assetId: rs?.data?.id,
          })),
        ),
      );
    }
    socketConfig.emit(
      ChannelRegister.SEND_INQUIRY_MESSAGE,
      JSON.stringify({
        type: ChannelRegister.SEND_INQUIRY_MESSAGE,
        content: {
          itemMessageGroupId: thread.itemMessageGroupId,
          sendSide: 1,
          sendStaffId: authUser?.id,
          message: text,
          isClosedInquiryMessage: 0,
          itemMessageImages: listUpload,
        },
      }),
    );
    setTimeout(() => refetchThreads(), 500);
  };

  useEffect(() => {
    if (data?.data?.content) {
      if (page == 1) {
        setListMesage(data?.data?.content as ItemInquiryMessages[]);
      } else {
        if (!skipLoad) {
          const messages = [...(data?.data?.content as ItemInquiryMessages[]), ...listMessage];
          setListMesage(messages);
          setSkipLoad(true);
        }
      }
      setTotalResponse(data?.data?.content?.length || 0);
      // prepend xong mới bỏ ngưng ghim đáy, tránh nhảy về đáy khi vừa tải tin cũ
      setTimeout(() => {
        pauseStickRef.current = false;
      }, 800);
    }
  }, [data]);

  // Tải trang cũ (trigger từ body card general hoặc container ngoài) → ngưng ghim đáy.
  useEffect(() => {
    if (page > 1) pauseStickRef.current = true;
  }, [page]);

  // useEffect(() => {
  //   if (dummy?.current && !isLoading && (page === 1 || scrollToEnd)) {
  //     document.getElementById('scrollMessage')?.scrollIntoView({ behavior: 'instant', block: 'end', inline: 'end' });
  //   }
  // }, [listMessage, dummy?.current, scrollToEnd]);
  // Cuộn TẤT CẢ section (.thread-card-body) + container chat tổng xuống đáy.
  // Chạy nhiều nhịp vì chiều cao còn đổi sau khi thread/ảnh trong tin nhắn render + load xong.
  const scrollAllToBottom = () => {
    if (pauseStickRef.current) return;
    const run = () => {
      document.querySelectorAll('.thread-card-body').forEach((el) => {
        (el as HTMLElement).scrollTop = (el as HTMLElement).scrollHeight;
      });
      const host = document.getElementById('scroll-message');
      if (host) {
        host.scrollTop = host.scrollHeight;
        host.scrollLeft = 0;
      }
    };
    requestAnimationFrame(run);
    [60, 160, 320, 600].forEach((ms) => setTimeout(run, ms));
  };

  // Ghim đáy khi khung chat cao lên vì lý do ngoài listMessage/threads: card thread vừa render,
  // ảnh trong tin nhắn load xong, preview file vừa chọn ở composer…
  // Thiếu cái này thì lần mở đầu tiên hay dừng ở giữa, không thấy tin mới nhất.
  useEffect(() => {
    const host = document.getElementById('scroll-message');
    if (!host) return;
    const observer = new MutationObserver((records) => {
      // Bỏ qua thay đổi trong hàng nhập liệu (icon gửi đổi mỗi lần gõ) — không phải nội dung chat.
      const hasContentChange = records.some((r) => !(r.target as HTMLElement)?.closest?.('.thread-card-composer'));
      if (hasContentChange) scrollAllToBottom();
    });
    observer.observe(host, { childList: true, subtree: true });
    const handleMediaLoad = (e: Event) => {
      if ((e.target as HTMLElement)?.tagName === 'IMG') scrollAllToBottom();
    };
    host.addEventListener('load', handleMediaLoad, true);
    return () => {
      observer.disconnect();
      host.removeEventListener('load', handleMediaLoad, true);
    };
  }, []);

  useLayoutEffect(() => {
    if (isLoading) return;
    if (page === 1 || scrollToEnd) scrollAllToBottom();
  }, [isLoading, page, scrollToEnd, listMessage.length]);

  // Tin mới / cập nhật trong section option → cuộn xuống đáy.
  // threads tính lại mỗi render → dùng signature ổn định (id + số item + at cuối) làm dep.
  const threadsSig = threads
    .map((t) => `${t.itemMessageGroupId}:${t.timeline?.length || 0}:${t.timeline?.[t.timeline.length - 1]?.at || ''}`)
    .join('|');
  useEffect(() => {
    scrollAllToBottom();
  }, [threadsSig]);

  // Thread お問い合わせ chung (optionType=null) — gửi tin thường vào group chung, không báo giá.
  const generalThread: ThreadTimeline = {
    itemMessageGroupId: groupSelect?.id as number,
    optionType: null,
    timeline: [],
  };

  // Cuộn lên đầu body card general (scroll nội bộ) → tải thêm tin cũ.
  const handleGeneralScroll = (e: any) => {
    if (isLoading) return;
    if (e?.currentTarget?.scrollTop === 0 && totalReponse >= size) {
      setSkipLoad(false);
      setPage((prev: number) => prev + 1);
    }
  };

  // Mốc thời gian hoạt động cuối của thread chung = tin nhắn cuối trong list.
  const generalLastAt = (listMessage as any[])?.length
    ? (listMessage as any[])[(listMessage as any[]).length - 1]?.createdAt
    : null;

  const generalCard = (
    <div className="thread-card thread-card--general" key="general">
      <div className="thread-card-head">
        <span className="thread-card-head-icon">
          <GeneralInquiryIcon />
        </span>
        <span>お問い合わせ</span>
      </div>
      <div
        id="general-thread-body"
        className="thread-card-body thread-card-body--general"
        onScroll={handleGeneralScroll}
      >
        {(listMessage as ItemInquiryMessages[])?.map((item) => {
          return (
            <Fragment key={item?.id}>
              {!item?.isClosedInquiryMessage ? (
                +item?.sendSide === 0 ? (
                  <div key={item?.id} className="item-detail_message_content_other">
                    <div className="item-detail_message_content_other_avatar">
                      {/* <img src={Helper.getSourceFile(authUser?.avatar?.source)} alt="alt.." /> */}
                      <Avatar size={32} src={Helper.getSourceFile(item?.sendUser?.avatar?.source)}></Avatar>
                    </div>
                    <div className="item-detail_message_content_other_text w-100">
                      {!!item?.itemMessageImages?.length && (
                        <div>
                          {/* <Avatar
                          size={32}
                          src={Helper?.getSourceFile(
                            item.sendUser?.avatar?.source || item.sendStaff?.avatar?.source,
                          )}
                        /> */}
                          <div className="d-flex flex-column">
                            <span className="item-detail_message-file">
                              <Image.PreviewGroup
                                preview={
                                  visibleReceiver
                                    ? {
                                        rootClassName: `image-preview__container ${!!item?.itemMessageImages?.length && item?.itemMessageImages?.length < 2 ? 'image-preview__container-limit' : ''} `,
                                        onChange: (current, prev) => {
                                          setPreviewReceiver({
                                            source: item?.itemMessageImages[current]?.source,
                                            index: current,
                                          });
                                        },
                                        src: Helper.getSourceFile(previewReceiver?.source),
                                        toolbarRender: (
                                          _,
                                          {
                                            transform: { scale },
                                            actions: { onRotateLeft, onRotateRight, onZoomOut, onZoomIn, onClose },
                                            current,
                                          },
                                        ) => (
                                          <div className="ant-image-preview-operations">
                                            <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                              <RotateLeftOutlined onClick={onRotateLeft} />
                                            </div>
                                            <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                              <RotateRightOutlined onClick={onRotateRight} />
                                            </div>
                                            <div
                                              className={`ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut ${scale === 1 && 'ant-image-preview-operations-operation-disabled'}`}
                                            >
                                              <ZoomOutOutlined disabled={true} onClick={onZoomOut} />
                                            </div>
                                            <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                              <ZoomInOutlined disabled={scale === 50} onClick={onZoomIn} />
                                            </div>
                                            <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                              <Divider type="vertical" className="bg-white" />
                                            </div>
                                            <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                              <CloseOutlined
                                                disabled={scale === 50}
                                                onClick={() => {
                                                  onClose();
                                                  // setVisibleReceiver(false)
                                                }}
                                              />
                                            </div>
                                          </div>
                                        ),
                                      }
                                    : false
                                }
                              >
                                {item.itemMessageImages?.map((item, index) => {
                                  return (
                                    <Image
                                      key={item?.id + index}
                                      onClick={() => {
                                        setVisibleReceiver(true);
                                        setPreviewReceiver({
                                          source: item?.source,
                                          index: +index + 1,
                                        });
                                      }}
                                      width={98}
                                      height={98}
                                      className="rounded"
                                      src={Helper.getSourceFile(item?.source)}
                                    />
                                  );
                                })}
                              </Image.PreviewGroup>
                            </span>
                            {/* <div className="d-flex flex-column">
                            <span className="color-sub-title font-weight-600 font-size-12 m-t-2">
                              {item.sendUser?.name || item.sendStaff?.loginId || item.sendUser?.emailAddress}
                            </span>
                            <span className="color-sub-cancel font-size-10 m-t-2">
                              {dayjs(item.createdAt).format(TIME_FORMAT.MESSAGE_TIME)}
                            </span>
                          </div> */}
                          </div>
                        </div>
                      )}
                      <p className="item-detail_message_content_other_text_content m-t-2">{item?.message}</p>
                      <p className="item-detail_message_content_other_text_name m-t-2">
                        {item?.sendUser?.name || item?.sendUser?.emailAddress}
                      </p>
                      <p className="item-detail_message_content_other_text_time">
                        {' '}
                        {dayjs(item?.createdAt).format(TIME_FORMAT.MESSAGE_TIME)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {item?.sendStaff?.role.includes(authUser?.role) ? (
                      <div
                        className={`item-detail_message_content_me ${item?.sendStaff?.role.includes(authUser?.role) && 'convert-me-send'}`}
                      >
                        <div
                          className={`item-detail_message_content_me_avatar ${item?.sendStaff?.role.includes(authUser?.role) && 'me-send'}`}
                        >
                          {/* <img src={Helper.getSourceFile(authUser?.avatar?.source)} alt="alt.." /> */}
                          <Avatar size={32} src={Helper.getSourceFile(authUser?.avatar?.source)}></Avatar>
                        </div>
                        <div className="item-detail_message_content_me_text w-100">
                          {!!item?.itemMessageImages?.length && (
                            <div>
                              <div className="d-flex flex-column align-items-end">
                                <span className="item-detail_message-file">
                                  <Image.PreviewGroup
                                    preview={
                                      visibleSender
                                        ? {
                                            rootClassName: `image-preview__container ${!!item?.itemMessageImages?.length && item?.itemMessageImages?.length < 2 ? 'image-preview__container-limit' : ''} `,
                                            onChange: (current, prev) => {
                                              setPreviewSender({
                                                source: item?.itemMessageImages[current]?.source,
                                                index: current,
                                              });
                                            },
                                            src: Helper.getSourceFile(previewSender?.source),
                                            toolbarRender: (
                                              _,
                                              {
                                                transform: { scale },
                                                actions: { onRotateLeft, onRotateRight, onZoomOut, onZoomIn, onClose },
                                              },
                                            ) => (
                                              <div className="ant-image-preview-operations">
                                                <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                                  <RotateLeftOutlined onClick={onRotateLeft} />
                                                </div>
                                                <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                                  <RotateRightOutlined onClick={onRotateRight} />
                                                </div>
                                                <div
                                                  className={`ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut ${scale === 1 && 'ant-image-preview-operations-operation-disabled'}`}
                                                >
                                                  <ZoomOutOutlined disabled={true} onClick={onZoomOut} />
                                                </div>
                                                <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                                  <ZoomInOutlined disabled={scale === 50} onClick={onZoomIn} />
                                                </div>
                                                <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                                  <Divider type="vertical" className="bg-white" />
                                                </div>
                                                <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut-close">
                                                  <CloseOutlined
                                                    disabled={scale === 50}
                                                    onClick={() => {
                                                      // setVisibleSender(false);
                                                      onClose();
                                                    }}
                                                  />
                                                </div>
                                              </div>
                                            ),
                                          }
                                        : false
                                    }
                                  >
                                    {item?.itemMessageImages?.map((item, index) => {
                                      return (
                                        <Image
                                          key={item?.id + index}
                                          onClick={() => {
                                            setVisibleSender(true);
                                            setPreviewSender({
                                              source: item?.source,
                                              index: +index + 1,
                                            });
                                          }}
                                          width={98}
                                          height={98}
                                          className="rounded"
                                          src={Helper.getSourceFile(item?.source)}
                                        />
                                      );
                                    })}
                                  </Image.PreviewGroup>
                                </span>
                                {/* <span className="color-sub-title font-weight-600 font-size-12 m-t-2">
                                {item?.sendStaff?.name || item?.sendStaff?.loginId}
                              </span>
                              <span className="color-sub-cancel font-size-10 m-t-2">
                                {dayjs(item?.createdAt).format(TIME_FORMAT.MESSAGE_TIME)}
                              </span> */}
                              </div>
                            </div>
                          )}
                          <div className="d-flex justify-content-end">
                            <p className="item-detail_message_content_me_text_content m-t-2">{item?.message}</p>
                          </div>
                          <p
                            className={`item-detail_message_content_me_text_name m-t-2 ${item?.sendStaff?.role.includes(authUser?.role) && 'me-send'}`}
                          >
                            {item?.sendStaff?.name || item?.sendStaff?.loginId}
                          </p>
                          <p
                            className={`item-detail_message_content_me_text_time ${item?.sendStaff?.role.includes(authUser?.role) && 'me-send'}`}
                          >
                            {dayjs(item?.createdAt).format(TIME_FORMAT.MESSAGE_TIME)}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`item-detail_message_content_me ${item?.sendStaff?.role.includes(authUser?.role) && 'convert-me-send'}`}
                      >
                        <div className="item-detail_message_content_me_avatar">
                          {/* <img src="/assets/icons/chatsupport.png" alt="" /> */}
                          <Avatar size={32} src={Helper?.getSourceFile(item.sendStaff?.avatar?.source)} />
                        </div>
                        <div className="item-detail_message_content_me_text w-100">
                          <p className="item-detail_message_content_me_text_content-other m-t-2">{item?.message}</p>
                          <p
                            className={`item-detail_message_content_me_text_name m-t-2 ${item?.sendStaff?.role.includes(authUser?.role) && 'me-send'}`}
                          >
                            {item?.sendStaff?.name || item?.sendStaff?.loginId}
                          </p>
                          <p
                            className={`item-detail_message_content_me_text_time ${item?.sendStaff?.role.includes(authUser?.role) && 'me-send'}`}
                          >
                            {dayjs(item?.createdAt).format(TIME_FORMAT.MESSAGE_TIME)}
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )
              ) : (
                <div className="item-detail_message_content_seen">
                  <p>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M4.125 13.125L9.375 18.375L19.875 7.125"
                        stroke="#514B4D"
                        strokeWidth="1.875"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>{intl.formatMessage({ id: 'chat-item.inquiry.closed' })}</span>
                  </p>
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
      <CardComposer
        showPrice={false}
        placeholder="メッセージを入力"
        onSend={(text, files) => handleThreadSend(generalThread, text, files)}
      />
    </div>
  );

  // Sort general + option theo hoạt động cuối; thread có tin mới nhất xuống dưới cùng (bump).
  const sortedCards: { kind: 'general' | 'thread'; at?: string | null; thread?: ThreadTimeline }[] = [
    { kind: 'general' as const, at: generalLastAt },
    ...threads.map((t) => ({
      kind: 'thread' as const,
      at: t.timeline?.length ? t.timeline[t.timeline.length - 1]?.at : null,
      thread: t,
    })),
  ].sort((a, b) => dayjs(a.at || 0).valueOf() - dayjs(b.at || 0).valueOf());

  return (
    <>
      {sortedCards.map((c) =>
        c.kind === 'general' ? (
          generalCard
        ) : (
          <InquiryThreadCard
            key={`thread-${c.thread!.itemMessageGroupId}`}
            thread={c.thread!}
            option={getInquiryOption(c.thread!.optionType)}
            staffById={staffById}
            onSend={(text, files, price) => handleThreadSend(c.thread!, text, files, price)}
            onComplete={(feeId) => completeFee.mutate(feeId)}
            onChangeType={(optionType) => requestTypeChange(c.thread!, optionType)}
          />
        ),
      )}
      <div className="p-t-12" ref={dummy} id="scrollMessage" style={{ height: '1px' }}></div>

      <CustomConfirmModel
        isOpen={!!pendingTypeChange}
        setIsOpen={() => setPendingTypeChange(undefined)}
        idDataDetail={0}
        titleText="オプション種別を変更"
        cancelText={intl.formatMessage({ id: 'common.cancel' })}
        confirmText={intl.formatMessage({ id: 'common.yes' })}
        messageText={
          <span className="font-size-16 color-sub-title">
            {`オプション種別を「${pendingTypeChange?.fromLabel}」から「${pendingTypeChange?.toOption?.label}」に変更しますか？`}
          </span>
        }
        onFinish={() => {
          if (!pendingTypeChange) return;
          changeThreadType.mutate({
            itemMessageGroupId: pendingTypeChange.thread.itemMessageGroupId,
            optionType: pendingTypeChange.toOption.value as ChangeThreadTypeDtoOptionTypeEnum,
          });
        }}
      />
    </>
  );
};
